import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../shared/helpers/icons';
import { HistoryComponent } from '../components/history/history.component';
import { IStudentInfo } from '../../shared/models/student';
import { IAssessCase } from '../../shared/models/assessCase';
import { AssessCaseService } from '../../shared/services/assessCase.service';
import { UserDataService } from '../../shared/services/auth/userData.service';
import { EStage, EStatus } from '../../shared/models/types.enum';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { ContactsService } from '../../shared/services/contact.service';
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { FormsModule } from '@angular/forms';
import { NgxPaginationModule } from 'ngx-pagination';
import { IThreatAssessmentTeam, IUser } from '../../shared/models/contact';
import { DownloadDocsComponent } from '../components/download-docs/download-docs.component';
import { Subject, takeUntil } from 'rxjs';
import { UsersnapComponent } from '../components/usersnap/usersnap.component';
import { CaseAssessTeamInfoComponent } from '../components/case-assess-team-info/case-assess-team-info.component';

declare var initFlowbite: any;

@Component({
  selector: 'app-cases',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    HistoryComponent,
    SpinnerComponent,
    FormsModule,
    DownloadDocsComponent,
    PopupMessageComponent,
    NgxPaginationModule,
    UsersnapComponent,
    CaseAssessTeamInfoComponent
  ],
  templateUrl: './cases.component.html',
  styleUrl: './cases.component.css',
})
export class CasesComponent implements OnInit {
  private destroy$ = new Subject<void>();
  stageName = EStage;
  statusEnum = EStatus;
  icons = Icons;

  searchQuery: string = '';
  usersList: IThreatAssessmentTeam[] = [];
  assessCases: IAssessCase[] = [];
  filteredAssessCases: IAssessCase[] = [];
  currentStudent: IStudentInfo | null = null;
  currentCase: IAssessCase | null = null;
  buildingId: string | null = null;
  messageModal: string | null = null;
  popupMessage: string | null = null;
  message: string | null = null;
  page = 1;
  itemsPerPage = 10;

  loading: boolean = false;
  sortOrder: boolean = true;
  showHistory: boolean = false;
  showDownload: boolean = false;
  showUsersnapWidget: boolean = false;
  showCaseThreatAssessmentTeam: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private userDataService: UserDataService,
    private assessCaseService: AssessCaseService,
    private contactsService: ContactsService
  ) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.buildingId = this.userDataService.getBuildingId();
      if (localStorage.getItem("assess_building_id")) { // get building Id from local storage
        this.buildingId = localStorage.getItem("assess_building_id");
        this.userDataService.setBuildingId(this.buildingId + ""); // set building Id from local storage
      }
    }
      
    this.loadAssessCases();
    this.loadUserInfo();
  } 

  loadAssessCases(): void {
    if (this.buildingId) {
      if (isPlatformBrowser(this.platformId)) {
        if (this.buildingId) {
          this.loading = true;
          this.assessCaseService.getBuildingAssessCases(this.buildingId!)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              this.loading = false;
              this.assessCases = response;
              this.filteredAssessCases = this.assessCases.length > 0 ? this.assessCases.reverse() : [];
              if (isPlatformBrowser(this.platformId)) {
                setTimeout(() => initFlowbite(), 100);
                setTimeout(() => this.showUsersnapWidget = true, 5000);
              }
            },
            error: (error) => {
              this.loading = false;
              console.error('Error fetching building assess cases:', error.message);
              this.popupMessage = error.message
              this.messageModal = 'unsuccess';
            },
          });
  
        }
      }
    }
  }

  sortList(key: keyof typeof this.filteredAssessCases[0]): void {
    this.sortOrder = !this.sortOrder;
    this.filteredAssessCases.sort((a, b) => {
      const valueA = a[key]?.toString().toLowerCase() || '';
      const valueB = b[key]?.toString().toLowerCase() || '';

      if (valueA < valueB) {
        return this.sortOrder ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortOrder ? 1 : -1;
      }
      return 0;
    });
  }
  
  loadUserInfo(): void {
    this.loading = true;
    this.contactsService.getUserInfo().subscribe({
      next: (user: IUser) => {
        this.loading = false;
        if (user) {
          this.userDataService.setCurrentUser(user);
        }
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading contact info:', error.message);
      }
    });
  }

  filterAssessCases(): void {
    this.filteredAssessCases = this.assessCases.filter((caseItem) => {
      return (
        caseItem.case_id.toLowerCase()
          .includes(this.searchQuery.toLowerCase()) ||
        caseItem.detail_of_situation?.toLowerCase()
          .includes(this.searchQuery.toLowerCase()) ||
        caseItem.created_by_name?.toLowerCase()
          .includes(this.searchQuery.toLowerCase())
      );
    });
  }

  handleOpenModal(modalType: string, clickedCase: IAssessCase): void {
    if (modalType === 'download') {
      this.showDownload = true;
    } else if (modalType === 'history') {
      this.showHistory = true;
    } else if (modalType === 'threat-assessment-team') {
      this.showCaseThreatAssessmentTeam = true;
    }
    this.currentCase = clickedCase ? clickedCase : null;
  }

  handleCloseModal(modalType: string): void {
    if (modalType === 'download') {
      this.showDownload = false;
    } else if (modalType === 'history') {
      this.showHistory = false;
    } else if (modalType === 'threat-assessment-team') {
      this.showCaseThreatAssessmentTeam = false;
    }
    this.currentCase = null;
  }
  
  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
